import styled from 'styled-components';

import style from '../../style';
import type { Size as ThemeSize } from '../../style/types/theme';
import { AnimationPosition, Size } from './types';

const getTransform = (position: AnimationPosition, size: ThemeSize) => {
  switch (position) {
    case AnimationPosition.BOTTOM:
      return {
        hidden: `translateY(${size.units(-2)})`,
        show: 'translateY(0)',
      };
    case AnimationPosition.TOP:
      return {
        hidden: `translateY(${size.units(2)})`,
        show: 'translateY(0)',
      };
    case AnimationPosition.RIGHT:
      return {
        hidden: `translateX(${size.units(-2)})`,
        show: 'translateX(0)',
      };
    case AnimationPosition.LEFT:
      return {
        hidden: `translateX(${size.units(2)})`,
        show: 'translateX(0)',
      };
    default:
      return {
        hidden: `translateY(${size.units(-2)})`,
        show: 'translateY(0)',
      };
  }
};

interface WrapperProps {
  $show: boolean;
}

export const Wrapper = styled.div<WrapperProps>(
  ({ $show, theme: { borderRadius, shadow } }) => ({
    overflow: 'hidden',
    borderRadius: borderRadius.medium,
    boxShadow: $show ? shadow.modal : 'none',
  }),
);

interface ContentWrapperProps {
  $show: boolean;
  $width: string;
  $maxWidth?: string;
  $animationPosition: AnimationPosition;
  $maxHeight?: number;
  $isDarkMode?: boolean;
}

export const ContentWrapper = styled.div<ContentWrapperProps>(
  ({
    $show,
    $animationPosition,
    $width,
    $maxWidth,
    $maxHeight,
    $isDarkMode,
    theme,
    theme: { size, color },
  }) => ({
    background: $isDarkMode
      ? color.background.reversed.default()
      : color.background.primary.default(),
    overflow: 'hidden',
    pointerEvents: $show ? 'auto' : 'none',
    height: 'auto',
    maxHeight: $maxHeight,
    width: $width,
    maxWidth: $maxWidth,
    display: 'flex',
    flexDirection: 'column',
    transform: $show
      ? getTransform($animationPosition, size).show
      : getTransform($animationPosition, size).hidden,
    opacity: $show ? 1 : 0,
    overflowY: $maxHeight ? 'auto' : undefined,
    ...style.mixins.scrollbar(theme),
    transition: $show
      ? 'all .25s cubic-bezier(0.4, 0, 0.2, 1.5)'
      : 'all .2s cubic-bezier(0.5, 0, 0.8, 1)',
  }),
);

interface ContainerProps {
  $size: Size;
}

export const Container = styled.div<ContainerProps>(({ theme: { size }, $size }) => ({
  padding: $size === Size.SMALL ? size.units(1) : size.units(2),
}));

export const HeaderComponentWrapper = styled.div(({ theme: { size } }) => ({
  paddingBottom: size.units(1.5),
}));

export const FooterComponentWrapper = styled.div(({ theme: { size } }) => ({
  paddingTop: size.units(1),
}));

interface ChildrenWrapperProps {
  $height?: number;
  $preventScroll?: boolean;
  $isDarkMode?: boolean;
}

export const ChildrenWrapper = styled.div<ChildrenWrapperProps>(
  ({ $height, $preventScroll, theme, $isDarkMode }) => ({
    overflow: $preventScroll ? 'hidden' : 'auto',
    overflowX: 'hidden',
    height: $height || 'auto',
    padding: $preventScroll ? undefined : `0 ${theme.size.units(0.5)}`,

    ...style.mixins.scrollbar(theme, $isDarkMode),
  }),
);
